import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Pestañas: Chile (CLP), Venezuela (VES) y USDT. */
export type PaisConciliacion = 'CL' | 'VE' | 'USDT';
export type MonedaConciliacion = 'CLP' | 'VES' | 'USDT';

/**
 * En qué quedó la plata que entró. Las claves son las del backend
 * (`conciliacion-abonos/types/conciliacion.types.ts`).
 */
export type EstadoEntrada = 'OK' | 'REP' | 'SINABONO' | 'RET' | 'SIN' | 'BANCO' | 'DIS' | 'NOREC';

export type OrigenEntrada =
  | 'CARTOLA'
  | 'SHINKANSEN'
  | 'N58'
  | 'BP'
  | 'R4'
  | 'BINANCE'
  | 'BINANCE_LINK'
  | 'USDT_MANUAL';

export interface ClienteAsignado {
  usuarioId: number;
  nombre: string;
  email: string | null;
  como: string;
}

export interface FilaEntrada {
  origen: OrigenEntrada;
  id: string;
  fecha: string;
  fechaBanco: string | null;
  horaBanco: string | null;
  cuenta: string;
  cuentaNombre: string;
  cuentaDetalle: string;
  nombre: string | null;
  documento: string | null;
  bancoOrigen: string | null;
  monto: number;
  moneda: MonedaConciliacion;
  referencia: string | null;
  historica: boolean;
  /** Abono manual o conversión: se muestra, pero no es plata que entró desde afuera. */
  ajuste: boolean;
  fuentes: string[];
  estado: EstadoEntrada;
  cliente: ClienteAsignado | null;
}

export interface PaginaEntradas {
  filas: FilaEntrada[];
  pagina: number;
  limite: number;
  total: number;
  desde: string;
  hasta: string;
  recortado: boolean;
}

export interface TotalEstado {
  estado: EstadoEntrada;
  cantidad: number;
  monto: number;
}

export interface TotalCuenta {
  cuenta: string;
  cuentaNombre: string;
  cuentaDetalle: string;
  cantidad: number;
  monto: number;
  montoOk: number;
  montoPendiente: number;
  propia: boolean;
  ajuste: boolean;
}

export interface ResumenEntradas {
  pais: PaisConciliacion;
  moneda: MonedaConciliacion;
  desde: string;
  hasta: string;
  recortado: boolean;
  /** Todas las filas (cuadra con la lista y los chips). */
  total: { cantidad: number; monto: number };
  /** Lo que entró desde afuera, sin ajustes. */
  entradas: {
    cantidad: number;
    monto: number;
    montoOk: number;
    /** Cuenta propia: Fábrica Rosa, traspasos entre cuentas, cargas del cajero. */
    montoPropio: number;
    /** USDT que nadie reclamó, en su mayoría tesorería. */
    montoSinReclamar: number;
  };
  /** Abonos manuales y conversiones. */
  ajustes: { cantidad: number; monto: number };
  porEstado: TotalEstado[];
  porCuenta: TotalCuenta[];
  porResolver: { cantidad: number; monto: number };
  /** Las cartolas asignadas antes de esta fecha se dan por acreditadas. */
  historicasAntesDe: string | null;
}

export interface PasoRecorrido {
  titulo: string;
  detalle: string;
  estado: 'ok' | 'pendiente' | 'no';
}

export interface DetalleEntrada {
  fila: FilaEntrada;
  pasos: PasoRecorrido[];
  motivo: string | null;
  accion: string | null;
}

export interface FiltrosConciliacion {
  pais: PaisConciliacion;
  desde?: string;
  hasta?: string;
  estados?: EstadoEntrada[];
  cuentas?: string[];
  buscar?: string;
  pagina?: number;
  limite?: number;
}

/**
 * Conciliación de abonos (solo operadores). La ruta del backend termina en
 * `-control` porque el balanceador corta con 403 todo lo que empieza con
 * `/admin`.
 */
@Injectable({ providedIn: 'root' })
export class ConciliacionAbonosService {
  private readonly apiUrl = `${environment.apiNest}conciliacion-abonos-control`;

  constructor(private http: HttpClient) {}

  private getHeaders(): HttpHeaders {
    return new HttpHeaders().set(
      'Authorization',
      `Bearer ${localStorage.getItem('token') || ''}`,
    );
  }

  /** Filtros -> query string. Las listas viajan separadas por coma. */
  private aParams(filtros: Partial<FiltrosConciliacion>): HttpParams {
    let params = new HttpParams();
    for (const [clave, valor] of Object.entries(filtros)) {
      if (valor === undefined || valor === null || valor === '') continue;
      if (Array.isArray(valor)) {
        if (valor.length) params = params.set(clave, valor.join(','));
        continue;
      }
      params = params.set(clave, String(valor));
    }
    return params;
  }

  resumen(pais: PaisConciliacion, desde?: string, hasta?: string): Observable<ResumenEntradas> {
    return this.http.get<ResumenEntradas>(`${this.apiUrl}/resumen`, {
      headers: this.getHeaders(),
      params: this.aParams({ pais, desde, hasta }),
    });
  }

  movimientos(filtros: FiltrosConciliacion): Observable<PaginaEntradas> {
    return this.http.get<PaginaEntradas>(`${this.apiUrl}/movimientos`, {
      headers: this.getHeaders(),
      params: this.aParams(filtros),
    });
  }

  detalle(origen: OrigenEntrada, id: string): Observable<DetalleEntrada> {
    return this.http.get<DetalleEntrada>(`${this.apiUrl}/detalle`, {
      headers: this.getHeaders(),
      params: this.aParams({ origen, id } as any),
    });
  }

  /**
   * Descarga el Excel con los filtros que se están viendo. No es un `<a href>`
   * porque el endpoint exige el token en la cabecera.
   */
  async descargar(filtros: FiltrosConciliacion): Promise<void> {
    let respuesta: HttpResponse<Blob>;
    try {
      respuesta = await firstValueFrom(
        this.http.get(`${this.apiUrl}/export`, {
          headers: this.getHeaders(),
          params: this.aParams({ ...filtros, pagina: undefined, limite: undefined }),
          responseType: 'blob',
          observe: 'response',
        }),
      );
    } catch (e: any) {
      // Con `responseType: 'blob'` el cuerpo del error también llega como
      // Blob: hay que leerlo para mostrar el mensaje del backend.
      throw new Error(await this.mensajeDeBlob(e));
    }
    this.guardarArchivo(respuesta, `conciliacion-abonos-${filtros.pais}.xlsx`);
  }

  private async mensajeDeBlob(e: any): Promise<string> {
    if (typeof e === 'string') return e;
    if (e?.status === 429) return 'Ya hay exportaciones en curso. Intenta de nuevo en un minuto.';
    const cuerpo = e?.error;
    if (cuerpo instanceof Blob) {
      try {
        const json = JSON.parse(await cuerpo.text());
        const msg = json?.message;
        if (typeof msg === 'string') return msg;
        if (Array.isArray(msg)) return msg.join(' · ');
      } catch {
        // No era JSON: se cae al mensaje genérico.
      }
    }
    return 'No se pudo generar el archivo. Intenta de nuevo.';
  }

  private guardarArchivo(respuesta: HttpResponse<Blob>, porDefecto: string): void {
    const blob = respuesta.body;
    if (!blob) throw new Error('La respuesta llegó vacía.');
    const cabecera = respuesta.headers.get('Content-Disposition') ?? '';
    const nombre = /filename="?([^";]+)"?/.exec(cabecera)?.[1] ?? porDefecto;

    const enlace = document.createElement('a');
    const url = URL.createObjectURL(blob);
    enlace.href = url;
    enlace.download = nombre;
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
    // Revocar justo detrás del click llega a cancelar la descarga.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}
